'use client';

import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { CLIENT_EVENT, logClientEvent } from '@/app/lib/datadog/client-config';
import {
	SERVER_ACTION_SKEW_EVENT,
	consumePendingServerActionSkew,
	reloadPage,
	type ServerActionSkewDetail,
} from '@/app/lib/serverActionSkew';
import Button from '@/ui/Button';

export const SERVER_ACTION_SKEW_TITLE = 'This page is out of date';
export const SERVER_ACTION_SKEW_BODY =
	'A new version of Praxis was released. Reload to continue. Anything you have not saved on this page will be lost.';

export const ServerActionSkewReloadView = () => (
	<div className="w-full max-w-md text-grey-900">
		<h2 id="server-action-skew-dialog-title" className="text-xl font-bold">
			{SERVER_ACTION_SKEW_TITLE}
		</h2>
		<p className="mt-3 text-grey-700">{SERVER_ACTION_SKEW_BODY}</p>
		<div className="mt-6 flex justify-end">
			<Button variant="contained" environment="light" onClick={reloadPage}>
				Reload
			</Button>
		</div>
	</div>
);

const ServerActionSkewDialog = () => {
	const [open, setOpen] = useState(false);
	const [detail, setDetail] = useState<ServerActionSkewDetail | null>(null);

	useEffect(() => {
		const onSkew = (event: CustomEvent<ServerActionSkewDetail>) => {
			if (event.detail) {
				setDetail(event.detail);
			}
			setOpen(true);
		};

		window.addEventListener(SERVER_ACTION_SKEW_EVENT, onSkew);
		const pending = consumePendingServerActionSkew();
		if (pending) {
			setDetail(pending);
			setOpen(true);
		}
		return () => window.removeEventListener(SERVER_ACTION_SKEW_EVENT, onSkew);
	}, []);

	useEffect(() => {
		if (!open) return;
		logClientEvent(CLIENT_EVENT.SERVER_ACTION_SKEW, detail ?? undefined, 'warn');
	}, [open]);

	return (
		<Dialog open={open} disableEscapeKeyDown aria-labelledby="server-action-skew-dialog-title">
			<DialogTitle id="server-action-skew-dialog-title" className="text-xl font-bold text-grey-900">
				{SERVER_ACTION_SKEW_TITLE}
			</DialogTitle>
			<DialogContent>
				<p className="text-grey-700">{SERVER_ACTION_SKEW_BODY}</p>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" environment="light" onClick={reloadPage}>
					Reload
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ServerActionSkewDialog;
